import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { SavedOrder } from "@/hooks/useOrders";

interface PendingProduct {
  productId: number;
  productName: string;
  productImage: string;
  orderRef: string;
}

interface Props {
  orders: SavedOrder[];
  userPhone: string;
}

export default function PendingReviewsBanner({ orders, userPhone }: Props) {
  const [pending, setPending] = useState<PendingProduct[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userPhone || orders.length === 0) { setLoading(false); return; }

    const check = async () => {
      // Only delivered orders
      const delivered = orders.filter(o => o.status === "delivered");
      if (delivered.length === 0) { setLoading(false); return; }

      // Collect all products from delivered orders
      const allProducts: PendingProduct[] = [];
      delivered.forEach(order => {
        order.items.forEach((item: { id: number; nameAr: string; image?: string }) => {
          allProducts.push({
            productId: item.id,
            productName: item.nameAr,
            productImage: item.image ?? "",
            orderRef: order.orderRef,
          });
        });
      });

      // Deduplicate by productId
      const unique = allProducts.filter(
        (p, idx, arr) => arr.findIndex(x => x.productId === p.productId) === idx
      );

      if (unique.length === 0) { setLoading(false); return; }

      // Check which ones already reviewed
      const productIds = unique.map(p => p.productId);
      const { data: existingReviews } = await supabase
        .from("reviews")
        .select("product_id")
        .eq("reviewer_phone", userPhone)
        .in("product_id", productIds);

      const reviewedIds = new Set((existingReviews ?? []).map(r => Number(r.product_id)));
      const notReviewed = unique.filter(p => !reviewedIds.has(p.productId));

      setPending(notReviewed);
      setLoading(false);
    };

    check();
  }, [orders, userPhone]);

  if (loading || dismissed || pending.length === 0) return null;

  return (
    <div className="relative overflow-hidden bg-gradient-to-l from-amber-400/8 via-amber-400/12 to-amber-500/8 border border-amber-400/30 rounded-2xl p-5 mb-4">
      {/* Glow */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-xl flex-shrink-0">
            <i className="ri-star-smile-line text-amber-400 text-xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="bg-amber-400 text-black text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}>
                {pending.length} {pending.length === 1 ? "منتج" : "منتجات"} بانتظار تقييمك
              </span>
            </div>
            <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
              قيّم مشترياتك واكسب نقاط مكافأة!
            </p>
            <p className="text-amber-400/70 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تقييمك يساعد العملاء الآخرين ويحسّن تجربة التسوق
            </p>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/5 text-gray-500 hover:text-white transition-colors cursor-pointer flex-shrink-0"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {/* Products list */}
      <div className="flex flex-col gap-2 relative z-10">
        {pending.slice(0, 3).map(p => (
          <Link
            key={p.productId}
            to={`/product/${p.productId}?review=1`}
            className="flex items-center gap-3 bg-black/20 hover:bg-black/30 border border-white/8 hover:border-amber-400/30 rounded-xl px-4 py-3 transition-all cursor-pointer group"
          >
            {/* Image */}
            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-[#1a1a1a] border border-white/5">
              {p.productImage ? (
                <img src={p.productImage} alt={p.productName} className="w-full h-full object-cover object-top" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-image-line text-gray-700 text-sm"></i>
                </div>
              )}
            </div>

            {/* Name */}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-bold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {p.productName}
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                طلب #{p.orderRef}
              </p>
            </div>

            {/* Stars preview */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {[1,2,3,4,5].map(s => (
                <i key={s} className="ri-star-line text-amber-400/30 group-hover:text-amber-400/60 text-xs transition-colors"></i>
              ))}
              <i className="ri-arrow-left-s-line text-gray-600 text-sm mr-1"></i>
            </div>
          </Link>
        ))}

        {pending.length > 3 && (
          <p className="text-amber-400/60 text-xs text-center py-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            +{pending.length - 3} منتجات أخرى بانتظار تقييمك
          </p>
        )}
      </div>

      {/* Stars decoration */}
      <div className="flex items-center gap-1 mt-3 relative z-10">
        {[1,2,3,4,5].map(s => (
          <i key={s} className="ri-star-fill text-amber-400/20 text-xs"></i>
        ))}
        <span className="text-amber-400/40 text-xs mr-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
          تقييماتك تُحدث فرقاً حقيقياً
        </span>
      </div>
    </div>
  );
}
